<script lang="typescript">
    import { fade } from 'svelte/transition'
	import { panzoom as realPanZoom } from './panzoom'
	import { createEventDispatcher } from 'svelte';

	const dispatch = createEventDispatcher();
  	const SEGMENT_LENGTH=50;
	const CLUE_RADIUS=10;
    const loc_for_index = (idx) => idx * SEGMENT_LENGTH;
    
	export let attempt;
	export let highlight = null;
	export let nopanzoom = false;
	
	let current_highlight = null;
	function showHighlight(h) {
		current_highlight = h;
	}

	$: showHighlight(highlight)

	let just_panned = false;
	
	function clueStatus(clue) {
		let count = attempt.edgesForVertex(clue).length;
		if (count < clue.value) return "low";
		if (count == clue.value ) return "equal";
		if (count > clue.value) return "high";
	}

	function nullPanZoom(node) {
		return {
			destroy() {}
		}
	}
	let panzoom;
	$: panzoom = nopanzoom ? nullPanZoom : realPanZoom;
	


	function onClick(col: number, row: number) {
		//sometimes after panning we get a click (mouse only), we need to ignore it. I wish I could think of a less hacky solution
		if (just_panned) return false;
		dispatch('squareClick', { location: { col: col, row: row }});	
	}

	//on pc, a drag results in a click that we can't cancel, we track whether we just finished a pan this event loop to prevent slant toggling
	function preventClick() {
		just_panned = true;
		//leave juspanned true until next loop
		window.setTimeout(() => { just_panned = false }, 1);
	}


	let cycleSlants = [];
	$: cycleSlants = attempt ? attempt.getCycles() : []

	function isInCycle(slants, col, row) {
		return !!slants.find(s => s.row == row && s.col == col)
	}

</script>


{#each [attempt.puzzle] as puzzle (puzzle)}
    <!-- force a new svg for each puzzle to reset our zoom etc -->
    <svg
        in:fade
        use:panzoom
        on:panend={() => preventClick()}
        image-rendering="crisp-edges"
        shape-rendering="crisp-edges"
        viewBox="{-SEGMENT_LENGTH - (Math.max(0, 5 - puzzle.width) / 2) * SEGMENT_LENGTH}
        {-SEGMENT_LENGTH - (Math.max(0, 5 - puzzle.height) / 2) * SEGMENT_LENGTH}
        {Math.max(7, puzzle.width + 2) * SEGMENT_LENGTH}
        {Math.max(7, puzzle.height + 2) * SEGMENT_LENGTH}"
        class="puzzle">
		<pattern id="stripe45" patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(-45)">
      		<line x1="0" y1="2" x2="4" y2="2" style="stroke:var(--grid-hint-color); stroke-width:1" />
			  <!--<line x1="2" y1="0" x2="2" y2="4" style="stroke:var(--grid-color); stroke-width:1" />-->
		</pattern>
		<filter id="glow" x="-100%" y="-100%" width="300%" height="300%" > 
			<feGaussianBlur stdDeviation="3.5" result="coloredBlur"/>
			<feMerge>
				<feMergeNode in="coloredBlur"/>
				<feMergeNode in="SourceGraphic"/>
			</feMerge>
		</filter>
        <g>

			<!--Hint-->
			{#if current_highlight}
				<g class="hint">
					<rect
							x={loc_for_index(current_highlight.col)}
							y={loc_for_index(current_highlight.row)}
							width={SEGMENT_LENGTH}
							height={SEGMENT_LENGTH}
							fill="url(#stripe45)"
							 />
				</g>
			{/if}

            <!--Grid-->
            {#each attempt.slants as cols, x_idx}
                {#each cols as row, y_idx}
                    <rect
                        x={loc_for_index(x_idx)}
                        y={loc_for_index(y_idx)}
                        width={SEGMENT_LENGTH}
                        height={SEGMENT_LENGTH}
                        class="cell-box"
                        on:click={(e) => onClick(x_idx, y_idx)} />
                {/each}
            {/each}
	


            <!--Slants-->
            {#each attempt.slants as slant_col, col_idx}
                {#each slant_col as slant, row_idx}
                    <!-- wrap in a group centered in the cell so we can rotate animate -->
                    <g
                        transform="translate({loc_for_index(col_idx) + SEGMENT_LENGTH / 2}
                        ,{loc_for_index(row_idx) + SEGMENT_LENGTH / 2})">
                        <line
                            class="slant {isInCycle(cycleSlants, col_idx, row_idx) ? 'cycle' : ''}
                            {slant == 1 ? 'positive' : ''}
                            {slant == 2 ? 'negative' : ''}"
                            x1={-SEGMENT_LENGTH / 2}
                            y1={-SEGMENT_LENGTH / 2}
                            x2={SEGMENT_LENGTH / 2}
                            y2={SEGMENT_LENGTH / 2} />
                    </g>
                {/each}
            {/each}

            <!--Clues-->
            {#each attempt.puzzle.clues as clue (clue)}
                <g class="clue {clueStatus(clue)}">
                    <circle
                        cx={loc_for_index(clue.x)}
                        cy={loc_for_index(clue.y)}
                        r={CLUE_RADIUS}
                        class="clue-circle" />
                    <text
                        x={loc_for_index(clue.x)}
                        y={loc_for_index(clue.y) + 0.1 * CLUE_RADIUS}
                        class="clue-text">
                        {clue.value}
                    </text>
                </g>
            {/each}
        </g>
    </svg>
{/each}

<style>
	.puzzle {
		background-color: var(--background-color);
		user-select: none;
		touch-action: manipulation;
		flex: 1;
	}

	.clue.low {
		--clue-color: var(--clue-color-low);
	}

	.clue.high {
		--clue-color: var(--clue-color-high);
	}

	.cell-box {
		stroke-width: 1;
		stroke: var(--grid-color);
		fill: var(--background-color);
		user-select: none;
		-webkit-tap-highlight-color: transparent;
		pointer-events: visible;
	}

	.clue-circle {
		stroke: var(--clue-color);
		fill:  var(--clue-color);
		stroke-width: 2;
		pointer-events: none;
	}

	.clue-text {
		fill: var(--clue-text-color);
		text-anchor: middle;
		vertical-align: middle;
		dominant-baseline: middle;
		pointer-events: none;
		user-select: none;
		font-family: sans-serif;
	}

	.slant {
		stroke: var(--slant-color);
		stroke-width: 3;
		opacity: 0;
		transform: rotateY(0deg);
		transition: all 0.1s linear;
		pointer-events: none;
		stroke-linecap: round;
	}
	.slant.cycle {
		stroke: red;
	}
	.slant.positive {
		opacity: 1;
		transform: rotateY(180deg);
	}
	.slant.negative {
		opacity: 1;
		transform: rotateY(0deg) scale(1);
	}
</style>